import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { systemSettingsService } from '../services/api';
import { SystemSettingRecord, SystemSettingValuesMap } from '../types';

interface SystemSettingsContextType {
  settings: SystemSettingRecord[];
  values: SystemSettingValuesMap;
  isLoading: boolean;
  error: string | null;
  refresh: (names?: string[]) => Promise<void>;
  getString: (name: string, fallback?: string) => string;
  getBoolean: (name: string, fallback?: boolean) => boolean;
  getNumber: (name: string, fallback?: number) => number;
}

const SystemSettingsContext = createContext<SystemSettingsContextType | undefined>(undefined);

function toBoolean(value: string | undefined, fallback = false): boolean {
  if (value === undefined || value === null) {
    return fallback;
  }

  const normalized = String(value).trim().toLowerCase();
  if (['true', '1', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['false', '0', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return fallback;
}

function toNumber(value: string | undefined, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function SystemSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SystemSettingRecord[]>([]);
  const [values, setValues] = useState<SystemSettingValuesMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async (names?: string[]) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await systemSettingsService.getPublic(names);
      setSettings(response.data.settings || []);
      setValues(response.data.values || {});
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load system settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const contextValue = useMemo<SystemSettingsContextType>(() => ({
    settings,
    values,
    isLoading,
    error,
    refresh,
    getString: (name: string, fallback = '') => values[name] ?? fallback,
    getBoolean: (name: string, fallback = false) => toBoolean(values[name], fallback),
    getNumber: (name: string, fallback = 0) => toNumber(values[name], fallback)
  }), [settings, values, isLoading, error]);

  return (
    <SystemSettingsContext.Provider value={contextValue}>
      {children}
    </SystemSettingsContext.Provider>
  );
}

export function useSystemSettings() {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error('useSystemSettings must be used within SystemSettingsProvider');
  }

  return context;
}